import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import useTheme from "../../../hooks/useTheme";

/**
 * Opening visible span for the starting interval.
 *
 * Only the embed's own tokens are legal: 1D, 5D, 1M, 3M, 6M, YTD, 12M, 60M,
 * ALL. Anything else is ignored and the widget's default window comes back.
 *
 * This is only the *opening* view. Once the chart is up, the interval and the
 * visible range belong to TradingView's own toolbar.
 */
const RANGE_FOR_INTERVAL = {
  "5S": "1D", "15S": "1D", "30S": "1D",
  "1": "1D", "3": "1D", "5": "1D", "15": "1D", "30": "1D",
  "1H": "5D", "2H": "5D", "4H": "1M", "12H": "1M",
  "D": "3M", "3D": "6M", "W": "12M", "M": "60M",
};

/**
 * TradingView chart for crypto, stocks and commodities.
 *
 * The controls are TradingView's own. There used to be a custom row above the
 * chart — an interval dropdown, a candles/line toggle and a "Tools" button —
 * duplicating the widget's toolbar, and it could only ever be the worse of the
 * two: changing interval meant tearing the whole widget down and rebuilding it,
 * where the native control switches in place, and it could not reflect anything
 * the trader changed inside the chart. The toolbar is shown now and the
 * duplicate row is gone.
 *
 * KNOWN TRADE-OFF — both theme variants are preloaded (see below) so a
 * light/dark flip is instant. The two iframes are independent and cross-origin,
 * so an interval or a drawing made in one is invisible to the other: flipping
 * the theme shows a chart still on the interval it was built with. The free
 * embed exposes no chart-state API, so they cannot be kept in sync. The
 * alternative is worse — rebuilding on each flip restores the reload that the
 * preload exists to remove.
 */
// TradingView series styles. The chart-type picker in the native toolbar can
// still switch away from this — it is only what the widget opens with.
const SERIES_STYLE = { bars: 0, candles: 1, line: 2, area: 3 };

function ProTradingView({ symbol, defaultInterval = "D", defaultStyle = SERIES_STYLE.candles }) {
  // One container per theme, both live at once.
  //
  // The embed bakes `theme` into its iframe URL, so a flip cannot be applied in
  // place. Rebuilding on every toggle meant watching the chart reload. Both
  // variants are built up front and kept mounted; flipping only changes which
  // one is visible. The cost is two iframes per chart.
  const THEME_SLOT = { light: 0, dark: 1 };
  const slotLight = useRef(null);
  const slotDark = useRef(null);
  const slots = [slotLight, slotDark];
  const widgets = useRef([null, null]);
  const [isMobile, setIsMobile] = useState(false);
  const { isDark } = useTheme();

  const containerIdRef = useRef(
    `pro-tv-${symbol.replace(/[^a-zA-Z0-9]/g, "-")}-${Math.random().toString(36).substr(2, 9)}`
  );
  const uniqueContainerId = containerIdRef.current;
  const slotIds = [`${uniqueContainerId}-light`, `${uniqueContainerId}-dark`];
  const active = isDark ? THEME_SLOT.dark : THEME_SLOT.light;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Build both theme variants. `isDark` is deliberately absent from the deps —
  // a theme flip must not rebuild anything, it only changes which slot shows.
  const buildWidget = useCallback(() => {
    if (!symbol) return;

    const init = () => {
      if (typeof window.TradingView === "undefined") return;

      // Single source of truth: the same token the page's own accents read.
      // The widget is an iframe and cannot read `--cat-accent` itself.
      const accent =
        getComputedStyle(document.documentElement)
          .getPropertyValue("--cat-accent")
          .trim() || "#5B1DEE";

      [THEME_SLOT.light, THEME_SLOT.dark].forEach((target) => {
        const host = slots[target].current;
        if (!host) return;
        const dark = target === THEME_SLOT.dark;

        if (widgets.current[target] && typeof widgets.current[target].remove === "function") {
          try { widgets.current[target].remove(); } catch (_) { /* ignore */ }
        }
        widgets.current[target] = null;
        host.innerHTML = "";

        widgets.current[target] = new window.TradingView.widget({
          container_id: slotIds[target],
          autosize: true,
          symbol,
          interval: defaultInterval,
          style: defaultStyle,
          timezone: "Etc/UTC",
          theme: dark ? "dark" : "light",
          locale: "en",
          range: RANGE_FOR_INTERVAL[defaultInterval] || "3M",

          overrides: {
            // TradingView's own dark pane is #131722; these pin it to the app's
            // --surface / --line so the chart sits flush in the card around it.
            ...(dark ? {
              "paneProperties.background": "#171A21",
              "paneProperties.backgroundType": "solid",
              "paneProperties.vertGridProperties.color": "#262B35",
              "paneProperties.horzGridProperties.color": "#262B35",
              "scalesProperties.textColor": "#9AA4B4",
              "scalesProperties.lineColor": "#2E3440",
            } : {}),
            // Line and area series take the market's category accent. Candles
            // are left alone: their green/red is up/down and must not shift.
            //
            // 8-digit hex (#RRGGBBAA) is NOT accepted — the override is dropped
            // and the series paints nothing, which is what made the line mode
            // render as an empty pane. Opacity is a separate `transparency`
            // property, 0-100, where 100 is fully transparent.
            "mainSeriesProperties.lineStyle.color": accent,
            "mainSeriesProperties.lineStyle.linewidth": 2,
            "mainSeriesProperties.areaStyle.linecolor": accent,
            "mainSeriesProperties.areaStyle.linewidth": 2,
            "mainSeriesProperties.areaStyle.color1": accent,
            "mainSeriesProperties.areaStyle.color2": accent,
            "mainSeriesProperties.areaStyle.transparency": 82,
          },

          // The native toolbar is the whole point: it carries the interval
          // buttons, the chart-type picker, indicators and the drawing tools.
          hide_top_toolbar: false,
          hide_side_toolbar: isMobile,   // the drawing rail needs width
          hide_legend: false,
          withdateranges: true,          // the range row under the plot
          allow_symbol_change: false,    // the market is chosen by the page
          save_image: true,
          details: false,
          extended_hours: false,

          enabled_features: [
            "side_toolbar_in_fullscreen_mode",
            "header_in_fullscreen_mode",
          ],
          disabled_features: [
            "display_market_status",
            "popup_hints",
            // Symbol search and compare would let the chart drift away from the
            // market the rest of the page is showing.
            "header_symbol_search",
            "header_compare",
          ],
          show_popup_button: false,
        });
      });
    };

    if (typeof window.TradingView === "undefined") {
      const script = document.createElement("script");
      script.src = "https://s3.tradingview.com/tv.js";
      script.async = true;
      script.onload = init;
      document.head.appendChild(script);
    } else {
      init();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, defaultInterval, defaultStyle, isMobile, uniqueContainerId]);

  useEffect(() => {
    buildWidget();
    return () => {
      widgets.current.forEach((w, i) => {
        if (w && typeof w.remove === "function") {
          try { w.remove(); } catch (_) { /* ignore */ }
        }
        widgets.current[i] = null;
      });
    };
  }, [buildWidget]);

  if (!symbol) {
    return (
      <div className="w-full h-64 flex items-center justify-center rounded-lg" style={{ background: "var(--surface-2)" }}>
        <p style={{ color: "var(--ink-2)" }}>No symbol provided</p>
      </div>
    );
  }

  return (
    <div className="w-full relative">
      {/* Both slots are always mounted and stacked; only the active one is
          visible. `visibility` rather than `display` — the widget needs a real
          box to lay itself out in while it loads off-screen.

          Taller than before: the native toolbar and the date-range row occupy
          vertical space that the custom controls used to take outside the
          chart, so the plot itself would otherwise shrink. */}
      <div className={`relative w-full ${isMobile ? "h-[340px]" : "h-[560px]"}`}>
        {[0, 1].map((i) => (
          <div
            key={slotIds[i]}
            id={slotIds[i]}
            ref={slots[i]}
            className="tradingview-widget-container absolute inset-0 w-full h-full"
            style={{
              visibility: active === i ? "visible" : "hidden",
              zIndex: active === i ? 1 : 0,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default memo(ProTradingView);
